import React, { useEffect, useState, useRef } from 'react';
import BowtieIcon from './BowtieIcon';
import styles from './UploadModal.module.css';

interface UploadStage {
  stage: 'uploading' | 'extracting' | 'processing' | 'completed';
  progress: number;
  message: string;
}

interface UploadModalProps {
  isOpen: boolean;
  currentStage: UploadStage | null;
  onClose: () => void;
}

const UploadModal: React.FC<UploadModalProps> = ({ isOpen, currentStage, onClose }) => {
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);
  const closeTimerRef = useRef<NodeJS.Timeout | null>(null);
  
  // Manage body overflow when modal mounts/unmounts
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    
    // Always clean up on unmount or when isOpen changes to false
    return () => {
      document.body.style.overflow = 'unset';
      
      // Clear any pending timers
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
    };
  }, [isOpen]);

  // Handle completion stage
  useEffect(() => {
    if (currentStage?.stage === 'completed' && isOpen) {
      setShowSuccessAnimation(true);
      
      // Clear any existing timer
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
      }
      
      // Close immediately with no delay
      closeTimerRef.current = setTimeout(() => {
        if (onClose && typeof onClose === 'function') {
          onClose();
        }
      }, 300); // Just enough time for the success animation to be visible
    }
    
    // Cleanup function
    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
    };
  }, [currentStage?.stage, isOpen, onClose]);
  
  // Reset showSuccessAnimation when modal closes
  useEffect(() => {
    if (!isOpen) {
      setShowSuccessAnimation(false);
    }
  }, [isOpen]);

  // Simple conditional rendering - only render when open
  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop that blurs everything except header and state hierarchy */}
      <div className={styles.backdrop} />
      
      {/* Modal content */}
      <div className={styles.modalContainer}>
        <div className={`${styles.modalContent} ${showSuccessAnimation ? styles.success : ''}`}>

          <div className={styles.modalHeader}>
            <BowtieIcon 
              width={64} 
              height={64} 
              className={`${styles.modalBowtie} ${!showSuccessAnimation ? styles.spinning : ''}`} 
            />
            <h2 className={styles.modalTitle}>
              {showSuccessAnimation ? 'Resume Uploaded Successfully!' : 'Processing Your Resume'}
            </h2>
          </div>

          {/* Progress stages visualization */}
          <div className={styles.progressStages}>
            <div className={`${styles.progressStage} ${currentStage && (currentStage.stage === 'uploading' || currentStage.progress >= 20) ? styles.active : ''}`}>
              <div className={styles.stageIconWrapper}>
                <div className={styles.stageIcon}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M7 18a4.6 4.4 0 0 1 0 -9a5 4.5 0 0 1 11 2h1a3.5 3.5 0 0 1 0 7h-1" />
                    <path d="M12 16V10m-3 3 3-3 3 3" />
                  </svg>
                </div>
                <div className={styles.stageGlow} />
              </div>
              <span className={styles.stageName}>Upload</span>
            </div>
            
            <div className={`${styles.stageConnector} ${currentStage && currentStage.progress >= 30 ? styles.active : ''}`}>
              <div className={styles.connectorFill} />
            </div>
            
            <div className={`${styles.progressStage} ${currentStage && (currentStage.stage === 'extracting' || currentStage.progress >= 40) ? styles.active : ''}`}>
              <div className={styles.stageIconWrapper}>
                <div className={styles.stageIcon}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                  </svg>
                </div>
                <div className={styles.stageGlow} />
              </div>
              <span className={styles.stageName}>Extract</span>
            </div>
            
            <div className={`${styles.stageConnector} ${currentStage && currentStage.progress >= 60 ? styles.active : ''}`}>
              <div className={styles.connectorFill} />
            </div>
            
            <div className={`${styles.progressStage} ${currentStage && (currentStage.stage === 'processing' || currentStage.progress >= 75) ? styles.active : ''}`}>
              <div className={styles.stageIconWrapper}>
                <div className={styles.stageIcon}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="16 18 22 12 16 6" />
                    <polyline points="8 6 2 12 8 18" />
                  </svg>
                </div>
                <div className={styles.stageGlow} />
              </div>
              <span className={styles.stageName}>Process</span>
            </div>
            
            <div className={`${styles.stageConnector} ${currentStage && currentStage.progress >= 90 ? styles.active : ''}`}>
              <div className={styles.connectorFill} />
            </div>
            
            <div className={`${styles.progressStage} ${currentStage && currentStage.stage === 'completed' ? styles.active : ''}`}>
              <div className={styles.stageIconWrapper}>
                <div className={styles.stageIcon}>
                  {showSuccessAnimation ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="20 6 9 17 4 12" className={styles.checkmark} />
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10" />
                    </svg>
                  )}
                </div>
                <div className={styles.stageGlow} />
              </div>
              <span className={styles.stageName}>Complete</span>
            </div>
          </div>

          {/* Progress details */}
          <div className={styles.progressDetails}>
            <div className={styles.progressBar}>
              <div 
                className={styles.progressFill} 
                style={{ width: `${currentStage?.progress || 0}%` }}
              />
              <div className={styles.progressShine} />
            </div>
            
            <div className={styles.progressInfo}>
              <p className={styles.progressMessage}>
                {currentStage?.message || 'Preparing...'}
              </p>
              <p className={styles.progressPercent}>
                {currentStage?.progress || 0}%
              </p>
            </div>
          </div>

          {/* Success animation overlay */}
          {showSuccessAnimation && (
            <div className={styles.successOverlay}>
              <div className={styles.successCircle} />
              <div className={styles.successBurst} />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default UploadModal;